import type { ShouldRevalidateFunction } from 'react-router';
import { CREATE_CONVERSATION_API } from '~/routes/remix-api.chat.create-conversation';
import { CREATE_CONVERSATION_MESSAGE_API } from '~/routes/remix-api.chat.create-conversation-message';
import { MARK_CONVERSATION_READ_API } from '~/routes/remix-api.chat.mark-conversation-read';
import { MARK_CONVERSATION_UNREAD_API } from '~/routes/remix-api.chat.mark-conversation-unread';
import { UPDATE_CONVERSATION_API } from '~/routes/remix-api.chat.update-conversation';
import { UPDATE_CONVERSATION_THUMBNAIL_API } from '~/routes/remix-api.chat.update-thumbnail';
import { LEAVE_CONVERSATION_API } from '~/routes/remix-api.chat.leave-conversation';
import { ADD_PARTICIPANTS_API } from '~/routes/remix-api.chat.add-participants';
import { REMOVE_PARTICIPANT_API } from '~/routes/remix-api.chat.remove-participant';
import { INVITE_TO_GROUP_CHAT_API } from '~/routes/remix-api.chat.invite-to-group-chat';
import { UPDATE_CONVERSATION_STATUS_API } from '~/routes/remix-api.chat.update-conversation-status';
import { MUTE_CONVERSATION_API } from '~/routes/remix-api.chat.mute';
import { UPDATE_CONVERSATION_SETTINGS_API } from '~/routes/remix-api.chat.update-settings';
import { REACT_MESSAGE_API } from '~/routes/remix-api.chat.react-message';
import { DELETE_MESSAGE_API } from '~/routes/remix-api.chat.delete-message';
import { UPDATE_NOTIFICATION_SETTINGS_API } from '~/routes/remix-api.settings.notifications';
import { SEND_MARKETING_PREFERENCES_EMAIL_API } from '~/routes/remix-api.settings.marketing-preferences-email';
import { ACTION_ADD_ITEMS_TO_MOOD_BOARD } from '~/routes/actions.add-items-to-mood-board';
import { REMOVE_INSPIRATION_ITEMS_BY_PAGE_API_ROUTE } from '~/routes/actions.remove-items-from-mood-board-by-page';
import { REMOVE_BUILD_HUB_INSPIRATION_API_ROUTE } from '~/routes/remix-api.app.build-hub-inspiration-remove';
import { CREATE_BUILD_HUB_INSPIRATION_API_ROUTE } from '~/routes/remix-api.app.build-hub-inspiration-create';
import { SUBSCRIPTION_PUBLICATIONS_API_ROUTE } from '~/routes/remix-api.subscription.publications';
import { PAGE_BUILDER_UPDATE_MODULE_DATA_ROUTE } from '~/routes/remix-api.page-builder.update-module-data';
import { SET_FEATURED_PROJECT } from '~/routes/remix-api.directory.set-featured-project';
import { SET_FEATURED_ARTICLE } from '~/routes/remix-api.directory.set-featured-article';
import { SET_PROJECT_IN_LIBRARY } from '~/routes/remix-api.directory.set-project-in-library';
import { SET_PRODUCT_IN_LIBRARY } from '~/routes/remix-api.directory.set-product-in-library';
import { DELETE_DIRECTORY_ITEM } from '~/routes/remix-api.directory.delete-directory-item';
import { TOGGLE_HOME_PIN } from '~/routes/remix-api.home.toggle-home-pin';
import { TOGGLE_FEATURED_BRAND_PIN } from '~/routes/remix-api.home.toggle-featured-brand-pin';
import { ENQUIRY_SUBMIT_API_ROUTE } from '~/routes/remix-api.enquiry.submit';
import { USER_SUBSCRIPTION_API_ROUTE } from '~/routes/remix-api.user.subscription';
import { MEMBER_UPDATE_SUBURB_API_ROUTE } from '~/routes/remix-api.member.update-suburb';
import { MEMBER_UPDATE_CONTACT_DETAILS_API_ROUTE } from '~/routes/remix-api.member.update-contact-details';
import { MARK_WEBSITE_NOTIFICATION_AS_READ_API_ROUTE } from '~/routes/remix-api.notifications.mark-as-read';
import { MARK_ALL_WEBSITE_NOTIFICATIONS_AS_READ_API_ROUTE } from '~/routes/remix-api.notifications.mark-all-as-read';
import { ADD_TO_SHOPPING_CART_API_ROUTE } from '~/routes/remix-api.ecommerce.add-to-shopping-cart';
import { UPDATE_SHOPPING_CART_ITEM_API_ROUTE } from '~/routes/remix-api.ecommerce.update-shopping-cart-item';
import { UPDATE_SHOPPING_CART_API_ROUTE } from '~/routes/remix-api.ecommerce.update-shopping-cart';

/** Sync saved module API route - doesn't need revalidation since it updates in place */
const SYNC_SAVED_MODULE_API = '/remix-api/page-builder/sync-saved-module';

/**
 * Routes that should not trigger a revalidation when the form is submitted.
 */
const NOT_REVALIDATE_ROUTES: readonly string[] = [
    CREATE_CONVERSATION_API,
    CREATE_CONVERSATION_MESSAGE_API,
    MARK_CONVERSATION_READ_API,
    MARK_CONVERSATION_UNREAD_API,
    UPDATE_CONVERSATION_API,
    UPDATE_CONVERSATION_THUMBNAIL_API,
    LEAVE_CONVERSATION_API,
    ADD_PARTICIPANTS_API,
    REMOVE_PARTICIPANT_API,
    INVITE_TO_GROUP_CHAT_API,
    UPDATE_CONVERSATION_STATUS_API,
    MUTE_CONVERSATION_API,
    UPDATE_CONVERSATION_SETTINGS_API,
    REACT_MESSAGE_API,
    DELETE_MESSAGE_API,
    SYNC_SAVED_MODULE_API,
    UPDATE_NOTIFICATION_SETTINGS_API,
    SEND_MARKETING_PREFERENCES_EMAIL_API,
    ACTION_ADD_ITEMS_TO_MOOD_BOARD,
    REMOVE_INSPIRATION_ITEMS_BY_PAGE_API_ROUTE,
    REMOVE_BUILD_HUB_INSPIRATION_API_ROUTE,
];

/**
 * Actions that change nothing the `_app.tsx` loader returns (member, cart,
 * notifications, toast, footer). Page edits, directory curation and board
 * creation: saved-board state lives in client atoms that `moodBoardPageIds`
 * only seeds on first load.
 */
const LAYOUT_NEUTRAL_ROUTES = [
    CREATE_BUILD_HUB_INSPIRATION_API_ROUTE,
    SUBSCRIPTION_PUBLICATIONS_API_ROUTE,
    PAGE_BUILDER_UPDATE_MODULE_DATA_ROUTE,
    SET_FEATURED_PROJECT,
    SET_FEATURED_ARTICLE,
    SET_PROJECT_IN_LIBRARY,
    SET_PRODUCT_IN_LIBRARY,
    DELETE_DIRECTORY_ITEM,
    TOGGLE_HOME_PIN,
    TOGGLE_FEATURED_BRAND_PIN,
];

/**
 * Same as above, but only for the root _app.tsx route.
 */
const ROOT_NOT_REVALIDATE_ROUTES = NOT_REVALIDATE_ROUTES.filter((route) => {
    return ![
        // Mark conversation read/unread will update the notification count.
        MARK_CONVERSATION_READ_API,
        MARK_CONVERSATION_UNREAD_API,
        // Marking conversation as archived/done may update the notification count.
        UPDATE_CONVERSATION_STATUS_API,
        // Leave conversation may update notification count.
        LEAVE_CONVERSATION_API,
        // Invite to group chat may update notification count.
        INVITE_TO_GROUP_CHAT_API,
        // Mute conversation may update notification count.
        MUTE_CONVERSATION_API,
    ].includes(route);
}).concat(LAYOUT_NEUTRAL_ROUTES);

/**
 * Cart edits after the item is in. The product page shows the cart summary in
 * its purchase drawer, so only it revalidates for these; no other page loader
 * reads the cart. Adding to the cart opens the drawer from the action's own
 * response, so no page revalidates for that.
 */
const CART_UPDATE_ROUTES = [
    UPDATE_SHOPPING_CART_ITEM_API_ROUTE,
    UPDATE_SHOPPING_CART_API_ROUTE,
];

/**
 * Same as {@link NOT_REVALIDATE_ROUTES}, for every public page loader (detail
 * pages and directories). These actions change no page data: an enquiry's sent
 * state and a newsletter sign-up live in the fetcher response, and their toasts
 * and any new session reach the screen through the `_app.tsx` loader, which
 * still revalidates for them.
 */
const PAGE_NEUTRAL_ROUTES = [
    ...NOT_REVALIDATE_ROUTES,
    ADD_TO_SHOPPING_CART_API_ROUTE,
    ENQUIRY_SUBMIT_API_ROUTE,
    USER_SUBSCRIPTION_API_ROUTE,
    SUBSCRIPTION_PUBLICATIONS_API_ROUTE,
    CREATE_BUILD_HUB_INSPIRATION_API_ROUTE,
    MEMBER_UPDATE_SUBURB_API_ROUTE,
    MEMBER_UPDATE_CONTACT_DETAILS_API_ROUTE,
    MARK_WEBSITE_NOTIFICATION_AS_READ_API_ROUTE,
    MARK_ALL_WEBSITE_NOTIFICATIONS_AS_READ_API_ROUTE,
];

/** Public pages other than the product page also ignore cart edits. */
const PAGE_NOT_REVALIDATE_ROUTES = [
    ...PAGE_NEUTRAL_ROUTES,
    ...CART_UPDATE_ROUTES,
];

/**
 * Check if a route loader should trigger a revalidation when a form is
 * submitted. If the form action is in a predefined list, the loader should not
 * trigger a revalidation. This is a performance optimization to avoid
 * unnecessary revalidations.
 * @param formAction The form action (route) to check.
 * @returns The route loader should not revalidate.
 * @see {@link shouldNotRevalidateRoot} for the root `_app.tsx` route.
 */
export function shouldNotRevalidate(formAction: string | undefined) {
    if (!formAction) {
        return false;
    }
    return NOT_REVALIDATE_ROUTES.includes(formAction);
}

/**
 * Check if the root `_app.tsx` route loader should trigger a revalidation when
 * a form is submitted. This is the same as `shouldNotRevalidate`, but only for
 * the root `_app.tsx` route.
 * @param formAction The form action (route) to check.
 * @returns The root `_app.tsx` route loader should not revalidate.
 * @see {@link shouldNotRevalidate} for routes except for the root `_app.tsx` route.
 */
export function shouldNotRevalidateRoot(formAction: string | undefined) {
    if (!formAction) {
        return false;
    }
    return ROOT_NOT_REVALIDATE_ROUTES.includes(formAction);
}

const isSearchPath = (pathname: string) =>
    pathname === '/search' || pathname.startsWith('/search/');

/** `/search`'s mobile loader returns a cut-down payload without the cart summary or footer. */
const leavesSearch = (currentUrl: URL, nextUrl: URL) =>
    isSearchPath(currentUrl.pathname) && !isSearchPath(nextUrl.pathname);

/**
 * Whether the root `_app.tsx` loader re-runs on a GET navigation. What the
 * shell shows from it (member, cart, notifications, footer) does not change
 * from page to page, so moving to another page keeps it and the actions that
 * change it revalidate it instead. Its URL-dependent fields stay as first
 * loaded until then: the page meta, which nothing reads at runtime, and the
 * site-wide top banner.
 *
 * It re-runs on a same-URL navigation (`useRevalidator`, or a link to the
 * current page) and when leaving `/search`, whose mobile loader returns a
 * cut-down payload without the cart summary or footer.
 *
 * Warm-ups use this to build the `_routes` list a navigation will request.
 */
export const appLayoutRevalidatesOnNavigation = (
    currentUrl: URL,
    nextUrl: URL,
): boolean => {
    if (
        currentUrl.pathname === nextUrl.pathname &&
        currentUrl.search === nextUrl.search
    ) {
        return true;
    }
    return leavesSearch(currentUrl, nextUrl);
};

/** `shouldRevalidate` for the root `_app.tsx` layout. */
export const shouldRevalidateAppLayout: ShouldRevalidateFunction = ({
    currentUrl,
    nextUrl,
    formMethod,
    formAction,
    defaultShouldRevalidate,
}) => {
    if (!formMethod) {
        return appLayoutRevalidatesOnNavigation(currentUrl, nextUrl);
    }
    // A GET submission is a navigation too; only leaving search needs the
    // layout, so re-submitting a form on the same page stays cheap.
    if (formMethod === 'GET') {
        return leavesSearch(currentUrl, nextUrl);
    }
    if (shouldNotRevalidateRoot(formAction)) {
        return false;
    }
    return defaultShouldRevalidate;
};

/**
 * `shouldRevalidate` for public page loaders: re-run on navigation and after
 * actions that can change page data (sign-in, page edits, tagging, directory
 * curation), but not after the member-neutral actions above.
 */
export const shouldRevalidatePage: ShouldRevalidateFunction = ({
    formAction,
    defaultShouldRevalidate,
}) => {
    if (formAction && PAGE_NOT_REVALIDATE_ROUTES.includes(formAction)) {
        return false;
    }
    return defaultShouldRevalidate;
};

/**
 * {@link shouldRevalidatePage} for the product page, which also re-runs after
 * cart edits because its purchase drawer shows the cart summary.
 */
export const shouldRevalidateProductPage: ShouldRevalidateFunction = ({
    formAction,
    defaultShouldRevalidate,
}) => {
    if (formAction && PAGE_NEUTRAL_ROUTES.includes(formAction)) {
        return false;
    }
    return defaultShouldRevalidate;
};
